import React, { useState } from "react";
import styled from "styled-components";
import { Accordion, AccordionSummary, AccordionDetails, Typography } from "@material-ui/core";
import { IPageSectionResponse } from "models/response/page-section-response";
import { SectionImage, SectionTitle } from "../custom-page.styles";

// ExpandMore Icon Component
const ExpandMoreIcon: React.FC = () => (
    <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="currentColor"
    >
        <path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" />
    </svg>
);

const AccordionContainer = styled.div`
  margin-bottom: 30px;
`;

const StyledAccordion = styled(Accordion)`
  margin-bottom: 10px !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
  border-radius: 8px !important;

  &:before {
    display: none;
  }

  &.Mui-expanded {
    margin-bottom: 10px !important;
  }
`;

const StyledAccordionSummary = styled(AccordionSummary)`
  background-color: #f5f5f5;
  border-radius: 8px !important;

  &.Mui-expanded {
    border-bottom-left-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }

  .MuiAccordionSummary-content {
    margin: 12px 0;
  }
`;

const QuestionText = styled(Typography)`
  font-weight: 600 !important;
  font-size: 1rem !important;
  color: #333;

  @media (max-width: 768px) {
    font-size: 1rem !important;
  }
`;

const StyledAccordionDetails = styled(AccordionDetails)`
  padding: 20px !important;
  background-color: #fff;
  display: flex !important;
  gap: 20px;
  align-items: flex-start;

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const AnswerItemImage = styled.img`
  width: 160px;
  height: 120px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;

  @media (max-width: 600px) {
    width: 100%;
    height: auto;
  }
`;

const AnswerTextWrapper = styled.div`
  flex: 1;
`;

const AnswerSubtitle = styled(Typography)`
  font-weight: 500 !important;
  font-size: 0.9rem !important;
  color: #777;
  margin-bottom: 6px !important;
`;

const AnswerText = styled(Typography)`
  font-size: 1rem !important;
  line-height: 1.7 !important;
  color: #555;

  @media (max-width: 768px) {
    font-size: 1rem !important;
  }
`;

const AnswerLink = styled.a`
  display: inline-block;
  margin-top: 12px;
  color: #1976d2;
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

interface AccordionFaqSectionProps {
    section: IPageSectionResponse;
}

export const AccordionFaqSection: React.FC<AccordionFaqSectionProps> = ({ section }) => {
    const [expanded, setExpanded] = useState<number | false>(false);

    const handleChange = (panel: number) => (_event: React.ChangeEvent<{}>, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    if (!section.items || section.items.length === 0) {
        return null;
    }

    const sortedItems = [...section.items].sort((a, b) => a.order - b.order);

    return (
        <AccordionContainer>
            {section.title && <SectionTitle>{section.title}</SectionTitle>}
            {section.imageUrl && <SectionImage src={section.imageUrl} alt={section.title || ''} />}
            {sortedItems.map((item, index) => (
                <StyledAccordion
                    key={item.id}
                    expanded={expanded === index}
                    onChange={handleChange(index)}
                >
                    <StyledAccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`panel${index}-content`}
                        id={`panel${index}-header`}
                    >
                        <QuestionText>{item.title || item.subtitle}</QuestionText>
                    </StyledAccordionSummary>
                    <StyledAccordionDetails>
                        {item.imageUrl && <AnswerItemImage src={item.imageUrl} alt={item.title || ''} />}
                        <AnswerTextWrapper>
                            {item.title && item.subtitle && <AnswerSubtitle>{item.subtitle}</AnswerSubtitle>}
                            <AnswerText>{item.description}</AnswerText>
                            {item.url && (
                                <AnswerLink href={item.url} target="_blank" rel="noopener noreferrer">
                                    Saiba mais
                                </AnswerLink>
                            )}
                        </AnswerTextWrapper>
                    </StyledAccordionDetails>
                </StyledAccordion>
            ))}
        </AccordionContainer>
    );
};
